import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Settings,
  Search,
  Filter,
  RefreshCw,
  User,
  Phone,
  Mail,
  MessageSquare,
  Clock,
  MoreHorizontal,
  ChevronRight,
  X,
} from 'lucide-react';
import { Card, Button, Input, useToast } from '@caruso/ui';
import { boardsService, BoardKanbanView, BoardKanbanCard, ExecutionDetails } from '@/services/boards.service';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Status colors and labels
const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  pending: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Pendente' },
  running: { color: 'bg-blue-500/20 text-blue-400', label: 'Em execucao' },
  completed: { color: 'bg-green-500/20 text-green-400', label: 'Concluido' },
  failed: { color: 'bg-red-500/20 text-red-400', label: 'Falhou' },
  paused: { color: 'bg-orange-500/20 text-orange-400', label: 'Pausado' },
  waiting_response: { color: 'bg-purple-500/20 text-purple-400', label: 'Aguardando' },
};

export function BoardKanbanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [draggingCard, setDraggingCard] = useState<string | null>(null);
  const [dragOverStep, setDragOverStep] = useState<string | null>(null);

  // Fetch kanban view
  const { data: kanban, isLoading, refetch } = useQuery({
    queryKey: ['board-kanban', id, search, statusFilter],
    queryFn: () =>
      boardsService.getKanbanView(id!, {
        search: search || undefined,
        status: statusFilter,
        limit: 100,
      }),
    enabled: !!id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch execution details when card is selected
  const { data: executionDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['execution-details', selectedCard],
    queryFn: () => boardsService.getExecutionDetails(selectedCard!),
    enabled: !!selectedCard,
  });

  // Move execution mutation
  const moveExecutionMutation = useMutation({
    mutationFn: ({ executionId, targetStepId }: { executionId: string; targetStepId: string }) =>
      boardsService.moveExecution(executionId, targetStepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board-kanban', id] });
      toast({
        title: 'Card movido com sucesso!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao mover card',
        variant: 'destructive',
      });
    },
  });

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, executionId: string) => {
    setDraggingCard(executionId);
    e.dataTransfer.setData('executionId', executionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingCard(null);
    setDragOverStep(null);
  };

  const handleDragOver = (e: React.DragEvent, stepId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStep(stepId);
  };

  const handleDragLeave = () => {
    setDragOverStep(null);
  };

  const handleDrop = (e: React.DragEvent, targetStepId: string) => {
    e.preventDefault();
    const executionId = e.dataTransfer.getData('executionId');
    if (executionId && targetStepId !== 'no-step') {
      moveExecutionMutation.mutate({ executionId, targetStepId });
    }
    setDraggingCard(null);
    setDragOverStep(null);
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold" />
      </div>
    );
  }

  if (!kanban) {
    return (
      <div className="text-center py-12">
        <p className="text-brand-ivory/60">Quadro nao encontrado</p>
        <Button onClick={() => navigate('/app/quadros')} className="mt-4">
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/quadros')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: kanban.board.color || '#6366f1' }}
              />
              <h1 className="text-3xl font-bold text-white">{kanban.board.name}</h1>
            </div>
            {kanban.board.description && (
              <p className="text-brand-ivory/60 mt-1">{kanban.board.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ivory/50" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar cliente..."
              className="pl-10 w-64"
            />
          </div>

          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value || undefined)}
            className="bg-brand-ink border border-brand-gold/20 rounded-md px-3 py-2 text-white text-sm"
          >
            <option value="">Todos os status</option>
            {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <Button variant="outline" size="icon" onClick={() => refetch()} title="Atualizar">
            <RefreshCw className="w-4 h-4" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(`/app/quadros/${id}/editar`)}
            title="Configurar"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto pb-4">
        <div className="flex gap-4 h-full min-w-max">
          {kanban.columns.map((column) => (
            <div
              key={column.step.id}
              className={`w-80 flex-shrink-0 flex flex-col rounded-lg border transition-colors ${
                dragOverStep === column.step.id
                  ? 'border-brand-gold bg-brand-gold/5'
                  : 'border-brand-gold/20 bg-brand-smoke'
              }`}
              onDragOver={(e) => handleDragOver(e, column.step.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.step.id)}
            >
              {/* Column Header */}
              <div className="p-4 border-b border-brand-gold/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: column.step.color || '#6366f1' }}
                    />
                    <h3 className="font-semibold text-white">{column.step.name}</h3>
                  </div>
                  <span className="text-brand-ivory/50 text-sm">
                    {column.totalCount}
                  </span>
                </div>
                {column.step.description && (
                  <p className="text-brand-ivory/50 text-xs mt-1 line-clamp-1">
                    {column.step.description}
                  </p>
                )}
              </div>

              {/* Column Cards */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {column.cards.length === 0 ? (
                  <div className="text-center py-8 text-brand-ivory/40 text-sm">
                    Nenhum card nesta coluna
                  </div>
                ) : (
                  column.cards.map((card) => (
                    <div
                      key={card.executionId}
                      draggable={column.step.id !== 'no-step'}
                      onDragStart={(e) => handleDragStart(e, card.executionId)}
                      onDragEnd={handleDragEnd}
                      onClick={() => setSelectedCard(card.executionId)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        draggingCard === card.executionId
                          ? 'opacity-50 border-brand-gold'
                          : 'border-brand-gold/10 hover:border-brand-gold/30 bg-brand-ink/50'
                      }`}
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-brand-gold/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-brand-gold" />
                          </div>
                          <div>
                            <p className="font-medium text-white text-sm">{card.client.name}</p>
                            <p className="text-brand-ivory/50 text-xs">{card.flow.name}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_CONFIG[card.status]?.color || 'bg-gray-500/20'}`}>
                          {STATUS_CONFIG[card.status]?.label || card.status}
                        </span>
                      </div>

                      {/* Card Contact Info */}
                      <div className="space-y-1 mb-2">
                        {card.client.phone && (
                          <div className="flex items-center gap-2 text-brand-ivory/60 text-xs">
                            <Phone className="w-3 h-3" />
                            <span>{card.client.phone}</span>
                          </div>
                        )}
                        {card.client.email && (
                          <div className="flex items-center gap-2 text-brand-ivory/60 text-xs truncate">
                            <Mail className="w-3 h-3" />
                            <span className="truncate">{card.client.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Card Footer */}
                      <div className="flex items-center justify-between text-xs text-brand-ivory/40">
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          <span>{card.messagesCount || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{formatTime(card.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Load More */}
              {column.totalCount > column.cards.length && (
                <div className="p-3 border-t border-brand-gold/10">
                  <Button variant="ghost" size="sm" className="w-full text-brand-ivory/60">
                    Ver mais ({column.totalCount - column.cards.length})
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Card Details Sidebar */}
      {selectedCard && (
        <div className="fixed inset-y-0 right-0 w-96 bg-brand-smoke border-l border-brand-gold/20 shadow-xl z-50 overflow-hidden flex flex-col">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-4 border-b border-brand-gold/10">
            <h2 className="text-lg font-semibold text-white">Detalhes</h2>
            <Button variant="ghost" size="icon" onClick={() => setSelectedCard(null)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoadingDetails ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-gold" />
              </div>
            ) : executionDetails ? (
              <div className="space-y-6">
                {/* Client Info */}
                <div>
                  <h3 className="text-white font-medium mb-3">Cliente</h3>
                  <Card className="p-4 bg-brand-ink/50 border-brand-gold/10">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-brand-gold" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{executionDetails.client.name}</p>
                        {executionDetails.currentStep && (
                          <span
                            className="text-xs px-2 py-0.5 rounded"
                            style={{
                              backgroundColor: `${executionDetails.currentStep.color}20`,
                              color: executionDetails.currentStep.color || '#6366f1',
                            }}
                          >
                            {executionDetails.currentStep.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {executionDetails.client.phone && (
                        <div className="flex items-center gap-2 text-brand-ivory/70">
                          <Phone className="w-4 h-4" />
                          <span>{executionDetails.client.phone}</span>
                        </div>
                      )}
                      {executionDetails.client.email && (
                        <div className="flex items-center gap-2 text-brand-ivory/70">
                          <Mail className="w-4 h-4" />
                          <span>{executionDetails.client.email}</span>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Execution Info */}
                <div>
                  <h3 className="text-white font-medium mb-3">Execucao</h3>
                  <Card className="p-4 bg-brand-ink/50 border-brand-gold/10 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-brand-ivory/60">Fluxo</span>
                      <span className="text-white">{executionDetails.flow.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-brand-ivory/60">Status</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_CONFIG[executionDetails.execution.status]?.color}`}>
                        {STATUS_CONFIG[executionDetails.execution.status]?.label || executionDetails.execution.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-brand-ivory/60">Iniciado</span>
                      <span className="text-white text-sm">
                        {executionDetails.execution.startedAt
                          ? formatTime(executionDetails.execution.startedAt)
                          : '-'}
                      </span>
                    </div>
                    {executionDetails.execution.error && (
                      <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
                        {executionDetails.execution.error}
                      </div>
                    )}
                  </Card>
                </div>

                {/* Messages */}
                <div>
                  <h3 className="text-white font-medium mb-3">
                    Mensagens ({executionDetails.messages.length})
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {executionDetails.messages.length === 0 ? (
                      <p className="text-brand-ivory/50 text-sm">Nenhuma mensagem</p>
                    ) : (
                      executionDetails.messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-3 rounded-lg text-sm ${
                            msg.direction === 'outbound'
                              ? 'bg-brand-gold/10 ml-4'
                              : 'bg-brand-ink mr-4'
                          }`}
                        >
                          <p className="text-white whitespace-pre-wrap">{msg.content}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-brand-ivory/50">
                            <span>{formatTime(msg.sentAt)}</span>
                            {msg.direction === 'outbound' && (
                              <>
                                {msg.delivered && <span>Entregue</span>}
                                {msg.read && <span>Lida</span>}
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Responses */}
                {executionDetails.responses.length > 0 && (
                  <div>
                    <h3 className="text-white font-medium mb-3">
                      Respostas ({executionDetails.responses.length})
                    </h3>
                    <div className="space-y-2">
                      {executionDetails.responses.map((resp) => (
                        <div key={resp.id} className="p-3 bg-brand-ink rounded-lg text-sm">
                          <p className="text-white">{resp.responseText}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs">
                            <span className="text-brand-ivory/50">{formatTime(resp.receivedAt)}</span>
                            {!resp.isValid && (
                              <span className="text-red-400">Invalida</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-brand-ivory/60 text-center">Erro ao carregar detalhes</p>
            )}
          </div>

          {/* Sidebar Footer */}
          {executionDetails && (
            <div className="p-4 border-t border-brand-gold/10">
              <Button
                className="w-full"
                onClick={() => navigate(`/app/clientes/${executionDetails.client.id}`)}
              >
                Ver Perfil do Cliente
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Backdrop for sidebar */}
      {selectedCard && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSelectedCard(null)}
        />
      )}
    </div>
  );
}
